// Admin Legal Pages — /admin/legal, permission: manage_settings
// Edits the Terms & Conditions and Refund & Cancellation Policy shown on
// /terms and /refund-policy. Saving new content or a new effective date bumps
// the page's version (users' recorded acceptances reference that version).
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { RichTextEditor } from '../../components/RichTextEditor';
import { toast } from 'sonner';
import { Loader2, Save, ExternalLink } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const PAGE_ORDER = ['terms', 'refund-policy'];

const LegalPageEditor = ({ page, headers, onSaved }) => {
  const [title, setTitle] = useState(page.title);
  const [effectiveDate, setEffectiveDate] = useState(page.effective_date || '');
  const [content, setContent] = useState(page.content);
  const [saving, setSaving] = useState(false);

  const dirty = title !== page.title
    || effectiveDate !== (page.effective_date || '')
    || content !== page.content;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/admin/legal/${page.slug}`,
        { title, effective_date: effectiveDate, content },
        { headers }
      );
      onSaved(data);
      toast.success('Page saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            {page.title} <Badge variant="outline">v{page.version}</Badge>
          </CardTitle>
          <CardDescription>
            Last updated {new Date(page.updated_at).toLocaleString()}
          </CardDescription>
        </div>
        <a
          href={`/${page.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-[#A67C52] hover:underline shrink-0"
        >
          View page <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
          <div className="space-y-2">
            <Label htmlFor={`${page.slug}-title`}>Title</Label>
            <Input id={`${page.slug}-title`} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${page.slug}-date`}>Effective Date</Label>
            <Input
              id={`${page.slug}-date`}
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Content</Label>
          <RichTextEditor value={content} onChange={setContent} />
        </div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            Changing the content or effective date creates a new version. Users accept the
            current version at signup and checkout.
          </p>
          <Button onClick={handleSave} disabled={!dirty || saving} data-testid={`save-legal-${page.slug}`}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminLegalPages = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/admin/legal`, { headers })
      .then((res) => {
        const list = res.data.pages || [];
        list.sort((a, b) => PAGE_ORDER.indexOf(a.slug) - PAGE_ORDER.indexOf(b.slug));
        setPages(list);
      })
      .catch(() => toast.error('Failed to load legal pages'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaved = (updated) => {
    setPages((prev) => prev.map((p) => (p.slug === updated.slug ? updated : p)));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-legal-pages">
      <div>
        <h1 className="text-3xl font-serif font-medium">Legal Pages</h1>
        <p className="text-muted-foreground">
          Terms &amp; Conditions and Refund &amp; Cancellation Policy shown on the website, at signup and at checkout.
        </p>
      </div>
      {pages.length > 0 && (
        <Tabs defaultValue={pages[0].slug}>
          <TabsList>
            {pages.map((p) => (
              <TabsTrigger key={p.slug} value={p.slug}>{p.title}</TabsTrigger>
            ))}
          </TabsList>
          {pages.map((p) => (
            <TabsContent key={p.slug} value={p.slug} className="mt-4">
              <LegalPageEditor key={`${p.slug}-${p.version}-${p.updated_at}`} page={p} headers={headers} onSaved={handleSaved} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default AdminLegalPages;
